import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "container",
    "input",
    "sendBtn",
    "marqueeContainer",
    "listContainer",
    "marqueeTrack",
    "messagesContainer"
  ]
  
  static values = {
    questions: Array,
    hasMessages: Boolean,
    startOpen: Boolean
  }

  connect() {
    this.initializeMarquee()
    this.initializeList()
    this.setupEventListeners()

    if (this.hasMessagesValue) {
      this.showChat()
    } else if (this.startOpenValue) {
      // If starting open, immediately show the list view (focused state)
      // We skip the animation by adding classes directly
      this.containerTarget.classList.add('focused')
      this.marqueeContainerTarget.classList.add('fade-out')
      this.listContainerTarget.classList.add('fade-in')
      
      // Notify parent to lock (keep open)
      this.notifyParent('locked')
    }

    // Send content heights to parent for proper sizing
    this.sendContentHeights()

    // Debounce tracking
    this.lastFocusTime = 0
    this.DEBOUNCE_MS = 250
  }

  sendContentHeights() {
    const numQuestions = this.questionsValue.length

    // Content-area has: padding-top: 12px, padding-bottom: 72px (space for absolute footer)
    // Collapsed: header(64) + content-area(12 + marquee:40 + 72) = 64 + 124 = 188
    // Expanded: hover(56) + header(64) + content-area(12 + list + 72)

    const listHeight = (numQuestions * 40) + ((numQuestions - 1) * 8)

    const collapsedHeight = 64 + 12 + 40 + 72 // = 188
    const expandedHeight = 56 + 64 + 12 + listHeight + 72

    console.log('[Expert QA] Heights:', { numQuestions, listHeight, collapsed: collapsedHeight, expanded: expandedHeight })

    window.parent.postMessage({
      type: 'ai_mode_content_heights',
      collapsed: collapsedHeight,
      expanded: expandedHeight
    }, '*')
  }

  initializeMarquee() {
    // Duplicate items for seamless loop (x4 for safety)
    const marqueeItems = [...this.questionsValue, ...this.questionsValue, ...this.questionsValue, ...this.questionsValue]
    
    this.marqueeTrackTarget.innerHTML = ''
    marqueeItems.forEach(q => {
      const chip = document.createElement('div')
      chip.className = 'chip'
      chip.textContent = q
      this.marqueeTrackTarget.appendChild(chip)
    })
  }

  initializeList() {
    this.listContainerTarget.innerHTML = ''
    this.questionsValue.forEach(q => {
      const btn = document.createElement('button')
      btn.className = 'list-item'
      btn.innerHTML = `
        <span>${q}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="arrow"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
      `
      btn.onclick = () => this.handleQuestionClick(q)
      this.listContainerTarget.appendChild(btn)
    })
  }

  setupEventListeners() {
    // We handle hover via CSS/events on the container in the view, 
    // but here we manage the "focused" state logic
    
    this.containerTarget.addEventListener('mouseenter', () => {
      if (!this.isChatMode) this.setFocus(true)
    })
    
    this.containerTarget.addEventListener('mouseleave', () => {
      if (!this.isChatMode && document.activeElement !== this.inputTarget && this.inputTarget.value.trim() === '') {
        this.setFocus(false)
      }
    })

    this.inputTarget.addEventListener('focus', () => {
      if (!this.isChatMode) this.setFocus(true)
    })

    this.inputTarget.addEventListener('blur', () => {
      if (!this.isChatMode && !this.containerTarget.matches(':hover')) {
        this.setFocus(false)
      }
    })

    this.inputTarget.addEventListener('input', (e) => {
      if (e.target.value.trim().length > 0) {
        this.sendBtnTarget.classList.remove('hidden')
      } else {
        this.sendBtnTarget.classList.add('hidden')
      }
    })

    this.inputTarget.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (this.inputTarget.value.trim().length > 0) {
          this.inputTarget.closest('form').requestSubmit()
        }
      }
    })
    
    // Listen for form submission to switch to chat mode
    const form = this.inputTarget.closest('form')
    if (form) {
      form.addEventListener('submit', () => {
        this.showChat()
      })
    }
  }

  setFocus(focused) {
    if (this.isChatMode) return

    if (focused) {
      this.lastFocusTime = Date.now()
      this.containerTarget.classList.add('focused')
      // Smooth fade transition between marquee and list
      this.marqueeContainerTarget.classList.add('fade-out')
      this.listContainerTarget.classList.add('fade-in')

      // Notify parent to lock (keep open) while focused
      this.notifyParent('locked')
    } else {
      // Debounce: don't unfocus within 250ms of focusing
      if (Date.now() - this.lastFocusTime < this.DEBOUNCE_MS) {
        return
      }

      // Only blur if input is empty
      if (this.inputTarget.value.trim() === '') {
        this.containerTarget.classList.remove('focused')
        // Smooth fade transition back
        this.marqueeContainerTarget.classList.remove('fade-out')
        this.listContainerTarget.classList.remove('fade-in')
        this.sendBtnTarget.classList.add('hidden')

        // Notify parent to unlock and collapse
        this.notifyParent('unlocked')
        this.notifyParent('collapse')
      }
    }
  }

  handleQuestionClick(q) {
    this.inputTarget.value = q
    // Auto-submit the query
    this.inputTarget.closest('form').requestSubmit()
  }
  
  showChat() {
    // If already in chat mode, do nothing
    if (this.isChatMode) return

    this.isChatMode = true
    this.containerTarget.classList.add('focused')
    this.containerTarget.classList.add('chat-mode')
    
    this.marqueeContainerTarget.classList.add('hidden')
    this.listContainerTarget.classList.add('hidden')
    this.listContainerTarget.classList.remove('fade-in')
    this.messagesContainerTarget.classList.remove('hidden')
    
    // Check if this is a fresh chat (no existing messages)
    // We can infer this if the messages container is empty or has no children
    // But we also have hasMessagesValue.
    // However, if we just submitted a form, hasMessagesValue might still be false until Turbo updates.
    // The "First Message" animation is primarily for the *transition* from empty to chat.
    
    if (!this.hasMessagesValue) {
      // Apply slide-up animation to the messages container or the first message
      // Since the message isn't there yet (it's being sent), we can animate the container itself
      // or wait for the message to arrive.
      // For now, let's animate the container to give a sense of "opening up"
      this.messagesContainerTarget.classList.add('animate-slide-up')
      
      // Remove the class after animation completes to avoid interfering with future scrolls
      setTimeout(() => {
        this.messagesContainerTarget.classList.remove('animate-slide-up')
      }, 600)
    }
    
    // Ensure we are locked open and expand to max height for chat
    this.notifyParent('chat_mode')
  }
  
  enterShowroom() {
    console.log('Navigate to full experience')
    this.notifyParent('lock')
    // You might want to actually navigate or do something else here
  }

  notifyParent(state) {
    // Communicate with ai-mode.js
    window.parent.postMessage({
      type: 'ai_mode_prompts_state',
      state: state
    }, '*')
  }
}
